import React, { useRef, useEffect, useCallback, useState } from 'react';
import { makeStyles } from 'utils/makeStylesAdapter';
import { Dialog } from '@mui/material';
import { DialogContent } from '@mui/material';
import { HeaderModal } from 'components';
import { colors } from 'utils/colorsSteps';
import AiAssistantService from 'services/ai-assistant/aiAssistantService';
import inquiryService from 'services/inquiries/inquiryService';
import { getEnvConfig } from 'configuration/envConfig';
import {
  MIME_TYPES,
  blobToDataUrl,
  getFileNameFromContentDisposition,
  getFileNameFromUrl,
  getMimeTypeFromExtension,
  getTimestampForFileName,
} from './utils';

const useStyles = makeStyles(() => ({
  root: {
    height: '100%',
  },
  iframe: {
    width: '100%',
    height: '85vh',
    border: 'none',
  },
}));

const downloadFileFromUrl = async (downloadUrl) => {
  try {
    const response = await AiAssistantService.downloadFile(downloadUrl);
    
    let blob;
    if (response.data instanceof Blob) {
      blob = response.data;
    } else {
      const contentType = response.headers['content-type'] || 'application/octet-stream';
      blob = new Blob([response.data], { type: contentType });
    }
    
    const disposition = response.headers['content-disposition'];
    const contentType = response.headers['content-type'] || blob.type;
    
    return await processBlobToFile(blob, disposition, contentType, downloadUrl);
  } catch (error) {
    console.error('Erro ao baixar arquivo da IA:', error);
    throw new Error('Não foi possível baixar o documento da IA. Verifique sua conexão e tente novamente.');
  }
};

const processBlobToFile = async (blob, disposition, contentType, downloadUrl) => {
  const originalFileName =
    getFileNameFromContentDisposition(disposition) ||
    getFileNameFromUrl(downloadUrl);

  const extensionFromName = originalFileName?.includes('.')
    ? originalFileName.split('.').pop()?.toLowerCase()
    : null;

  const extensionFromMime = Object.keys(MIME_TYPES).find(ext => MIME_TYPES[ext] === contentType);
  const extension = extensionFromName || extensionFromMime || 'docx';
  const fileName = `peça-gerada-${getTimestampForFileName()}.${extension}`;

  const mimeType = contentType || getMimeTypeFromExtension(fileName);
  const base64 = await blobToDataUrl(blob);

  return {
    fileObj: {
      name: fileName,
      type: mimeType,
      lastModified: Date.now(),
    },
    base64Obj: {
      name: fileName,
      base64,
      mimeType,
    },
  };
};

const ModalAiAssistant = props => {
  const classes = useStyles();
  const { onClose, open, inquiryId, isPM, userType, setFileInBase64, setUploadObject, policeInquiryNumber } = props;
  
  const [files, setFiles] = useState([]);
  const iframeRef = useRef(null);
  const isMountedRef = useRef(true);
  const apiKeyRef = useRef(null);

  const aiUrl = getEnvConfig('REACT_APP_AI_IFRAME_URL');
  console.log('AI Iframe URL:', aiUrl);

  useEffect(() => {
    isMountedRef.current = true;
    
    if (open && inquiryId) {
      inquiryService
        .getAllFilesInquiryService(inquiryId, isPM, userType)
        .then(response => {
          if (isMountedRef.current) {
            setFiles(response.data);
          }
        })
        .catch(() => {
          if (isMountedRef.current) {
            setFiles([]);
          }
        });
    }
    
    return () => {
      isMountedRef.current = false;
    };
  }, [open, inquiryId, isPM, userType]);

  const processFile = (fileData) => {
    if (fileData.url) {
      return {
        name: fileData.name,
        url: fileData.url,
        mimeType: fileData.mimetype || getMimeTypeFromExtension(fileData.name),
      };
    }
    
    if (fileData.base64) {
      let mimeType = fileData.mimeType || fileData.type;
      
      if (!mimeType && fileData.base64.startsWith('data:')) {
        const match = fileData.base64.match(/^data:([^;]+);/);
        if (match) mimeType = match[1];
      }
      
      if (!mimeType) {
        mimeType = getMimeTypeFromExtension(fileData.name || fileData.fileName);
      }
      
      return {
        name: fileData.name || fileData.fileName,
        url: fileData.base64,
        mimeType,
      };
    }
    
    return null;
  };

  const sendInitToIframe = useCallback(async () => {
    const node = iframeRef.current;
    if (!node?.contentWindow || !isMountedRef.current) {
      return;
    }
    
    let targetOrigin = '*';
    try {
      targetOrigin = new URL(aiUrl).origin;
    } catch {
      // URL inválida: mantém o targetOrigin padrão
    }
    
    const token = localStorage.getItem('token');
    let apiKey = apiKeyRef.current;
    
    if (!apiKey) {
      try {
        const resp = await AiAssistantService.getAiServiceApiKey();
        apiKey = resp?.data?.apiKey ?? null;
        apiKeyRef.current = apiKey;
      } catch {
        // sem api key: segue sem enviar a chave para o iframe
      }
    }

    if (!isMountedRef.current || !iframeRef.current?.contentWindow) {
      return;
    }

    const filesToSend = (files || []).map(processFile).filter(Boolean);

    const message = {
      type: 'INIT',
      payload: {
        token,
        apiKey,
        files: filesToSend.filter(Boolean),
        inquiryNumber: policeInquiryNumber,
      },
    };
    
    iframeRef.current.contentWindow.postMessage(message, targetOrigin);
  }, [files, aiUrl, policeInquiryNumber]);

  useEffect(() => {
    const onMessage = async event => {
      if (!isMountedRef.current) {
        return;
      }
      
      const { type, payload } = event.data || {};
      
      if (type === 'READY') {
        sendInitToIframe();
        return;
      }

      if (type === 'DOCUMENT_FINALIZED' && payload?.downloadUrl) {
        const { legalFilingId, downloadUrl } = payload;

        try {
          const downloadedFile = await downloadFileFromUrl(
            downloadUrl,
            legalFilingId,
            apiKeyRef.current,
          );

          setUploadObject(prev => {
            const exists = prev?.some(upload => upload.name === downloadedFile.fileObj.name);
            return exists ? prev : [...(prev || []), downloadedFile.fileObj];
          });

          setFileInBase64(prev => {
            const exists = prev?.some(upload => upload.name === downloadedFile.base64Obj.name);
            return exists ? prev : [...(prev || []), downloadedFile.base64Obj];
          });
        } catch (error) {
          console.error(error);
        }
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [sendInitToIframe, setFileInBase64, setUploadObject]);

  return (
    <Dialog
      onClose={onClose}
      open={open}
      aria-labelledby="ai-assistant-dialog-title"
      className={classes.root}
      maxWidth="lg"
      fullWidth
      PaperProps={{ style: { maxWidth: 2400 } }}
      scroll="paper"
    >
      <HeaderModal colorIconTitle="#fff" backgroundColor={colors.navBar} onClose={onClose}>
        Assistente de IA — Criar peça
      </HeaderModal>
      <DialogContent dividers>
        <iframe
          ref={iframeRef}
          className={classes.iframe}
          height="70vh"
          title="ai-assistant"
          src={aiUrl}
        />
      </DialogContent>
    </Dialog>
  );
};

export default ModalAiAssistant;